import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  Fade,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Alert,
  Skeleton,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import BrushIcon from '@mui/icons-material/Brush';
import FileDropZone from './FileDropZone';
import ConfirmDialog from './ConfirmDialog';
import type { EditImageHandler } from './TipTapEditor';
import { filesService } from '../../services/filesService';
import type { FileApi } from '../../services/sandboxService';
import { FileEntity } from '../../types';
import { useNotification } from '../../hooks/NotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { FILES_DELETE } from '../../constants/permissions';
import { useImageVersion, withImageVersion } from '../../utils/imageVersionStore';

/** Aktiver Thumbnail-Rand in der Lightbox — wie Audit-Demo (`#3dd47e`). */
const LIGHTBOX_THUMB_ACTIVE = '#3dd47e';

/** Bild mit Cache-Busting nach Bearbeitungen (gleiche URL, neuer Inhalt). */
const VersionedImg: React.FC<{ fileId: number; src: string; alt: string; sx: SxProps<Theme> }> = ({ fileId, src, alt, sx }) => {
  const version = useImageVersion(fileId);
  return <Box component="img" src={withImageVersion(src, version)} alt={alt} sx={sx} />;
};

const isEditedVersion = (file: FileEntity): boolean => Boolean(file.original_file_id || file.annotationen);

/**
 * Bearbeitete Versionen direkt hinter ihrem Original einsortieren; Reihenfolge
 * der Originale bleibt (Upload-Reihenfolge = ID).
 */
const sortWithEditedAfterOriginal = (files: FileEntity[]): FileEntity[] => {
  const byOriginal = new Map<number, FileEntity[]>();
  const roots: FileEntity[] = [];
  const ids = new Set(files.map((f) => f.id));
  for (const f of files) {
    if (f.original_file_id && ids.has(f.original_file_id)) {
      const list = byOriginal.get(f.original_file_id) ?? [];
      list.push(f);
      byOriginal.set(f.original_file_id, list);
    } else {
      roots.push(f);
    }
  }
  const result: FileEntity[] = [];
  for (const root of roots) {
    result.push(root);
    const edited = byOriginal.get(root.id);
    if (edited) result.push(...edited);
  }
  return result;
};

interface ScreenshotGalleryProps {
  schwachstelleId: number | null;
  onInsertImage?: (fileId: number) => void;
  onInsertCodeBlock?: (fileId: number) => void;
  editable?: boolean;
  onFilesLoaded?: (files: FileEntity[]) => void;
  /** HTML der Schwachstellen-Textfelder: ermittelt „im Text referenziert“ wie in der Demo-Ansicht. */
  referenceHtml?: string;
  /** Alternative Datei-API (z.B. isolierte Umgebung ueber /api/restricted). Default: filesService. */
  fileApi?: FileApi;
  /** Loeschen unabhaengig von FILES_DELETE erlauben (isolierte Umgebung: eigene Dateien). */
  allowDelete?: boolean;
  /** Bild im Annotations-Editor bearbeiten (Original bleibt erhalten). */
  onEditImage?: EditImageHandler;
  /** Aenderung erzwingt Neuladen der Dateiliste (z. B. nach Bildbearbeitung). */
  refreshToken?: number;
}

const ScreenshotGallery: React.FC<ScreenshotGalleryProps> = ({
  schwachstelleId,
  onInsertImage,
  onInsertCodeBlock,
  editable = false,
  onFilesLoaded,
  referenceHtml = '',
  fileApi,
  allowDelete,
  onEditImage,
  refreshToken,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const api: FileApi = fileApi ?? filesService;
  const canDeleteFiles = allowDelete ?? hasPermission(FILES_DELETE);
  const [files, setFiles] = useState<FileEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileEntity | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [editingFileId, setEditingFileId] = useState<number | null>(null);

  const loadFiles = useCallback(async () => {
    if (!schwachstelleId) return;
    try {
      setLoading(true);
      const data = sortWithEditedAfterOriginal(await api.list({ schwachstelle_id: schwachstelleId }));
      setFiles(data);
      if (onFilesLoaded) onFilesLoaded(data);
    } catch (error) {
      console.error('Fehler beim Laden der Screenshots:', error);
    } finally {
      setLoading(false);
    }
  }, [schwachstelleId, onFilesLoaded]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles, refreshToken]);

  const canEditImages = editable && Boolean(onEditImage);

  const handleEditImage = useCallback(
    async (file: FileEntity) => {
      if (!onEditImage || editingFileId !== null) return;
      setEditingFileId(file.id);
      try {
        // Neuladen der Liste uebernimmt der Aufrufer via refreshToken
        await onEditImage(file.id, { allowInsert: Boolean(onInsertImage) });
      } finally {
        setEditingFileId(null);
      }
    },
    [onEditImage, editingFileId, onInsertImage],
  );

  const uploadScreenshotFiles = useCallback(async (incomingFiles: File[]) => {
    if (!schwachstelleId) return;
    if (incomingFiles.length === 0) return;

    try {
      setUploading(true);
      for (const file of incomingFiles) {
        await api.upload(file, 'screenshot', undefined, undefined, schwachstelleId);
      }
      await loadFiles();
      showSuccess(
        incomingFiles.length === 1
          ? 'Screenshot erfolgreich hochgeladen'
          : `${incomingFiles.length} Screenshots erfolgreich hochgeladen`
      );
    } catch (error) {
      console.error('Fehler beim Hochladen:', error);
      showError('Screenshot konnte nicht hochgeladen werden');
    } finally {
      setUploading(false);
    }
  }, [loadFiles, schwachstelleId, showError, showSuccess]);

  const handleFilesDropped = useCallback(async (droppedFiles: File[]) => {
    await uploadScreenshotFiles(droppedFiles);
  }, [uploadScreenshotFiles]);

  const handleClipboardPasteError = useCallback((message: string) => {
    showError(message);
  }, [showError]);

  useEffect(() => {
    if (!editable || !schwachstelleId) return;

    const handleDocumentPaste = (event: ClipboardEvent) => {
      if (uploading) return;

      const activeElement = document.activeElement as HTMLElement | null;
      const isTextInputFocused = !!activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable ||
        !!activeElement.closest('[contenteditable="true"], [role="textbox"], .ProseMirror')
      );
      if (isTextInputFocused) return;

      const items = Array.from(event.clipboardData?.items || []);
      const imageFiles = items
        .filter((item) => item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((file): file is File => !!file);

      if (imageFiles.length === 0) return;

      event.preventDefault();
      void uploadScreenshotFiles(imageFiles);
    };

    document.addEventListener('paste', handleDocumentPaste);
    return () => {
      document.removeEventListener('paste', handleDocumentPaste);
    };
  }, [editable, schwachstelleId, uploading, uploadScreenshotFiles]);

  const handleDeleteClick = (file: FileEntity) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await api.delete(fileToDelete.id);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      await loadFiles();
    } catch (error) {
      console.error('Fehler beim Loeschen:', error);
    }
  };

  const handleInsertImage = (file: FileEntity) => {
    if (onInsertImage) onInsertImage(file.id);
  };

  const handleInsertCodeBlock = (file: FileEntity) => {
    if (onInsertCodeBlock) onInsertCodeBlock(file.id);
  };

  const isImageFile = (filename: string): boolean => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
  };

  const isCodeFile = (file: FileEntity): boolean =>
    file.typ === 'BurpRequest' ||
    ['.txt', '.http', '.har', '.log', '.json', '.xml'].includes(
      '.' + (file.dateiname.split('.').pop()?.toLowerCase() || ''),
    );

  const imageFiles = files.filter((f) => isImageFile(f.dateiname));
  const nonImageFiles = files.filter((f) => !isImageFile(f.dateiname));

  const usedFileIds = useMemo(() => {
    const combined = referenceHtml || '';
    const ids = new Set<number>();
    const re = /\/files\/(\d+)\/preview/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(combined)) !== null) ids.add(Number(m[1]));
    return ids;
  }, [referenceHtml]);

  const openLightboxAtIndex = (idx: number) => {
    if (idx < 0 || idx >= imageFiles.length) return;
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  useEffect(() => {
    if (!lightboxOpen || imageFiles.length <= 1) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setLightboxIndex((prev) => (prev - 1 + imageFiles.length) % imageFiles.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setLightboxIndex((prev) => (prev + 1) % imageFiles.length);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, imageFiles.length]);

  const borderColor = isDark ? alpha(theme.palette.divider, 0.3) : theme.palette.divider;

  if (!schwachstelleId) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <PhotoLibraryIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Screenshots / Anhänge
            </Typography>
          </Box>
          <Alert severity="info">
            Bitte zuerst speichern, um Screenshots hochladen zu können.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <PhotoLibraryIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Screenshots / Anhänge
          </Typography>
          {imageFiles.length > 0 && (
            <Chip
              label={imageFiles.length}
              size="small"
              sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }}
            />
          )}
          {imageFiles.length === 0 && nonImageFiles.length > 0 && (
            <Chip
              label={nonImageFiles.length}
              size="small"
              sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>

        {/* Upload area - only in edit mode */}
        {editable && (
          <Box sx={{ mb: 2 }}>
            <FileDropZone
              data-testid="screenshot-dropzone"
              onFilesDropped={handleFilesDropped}
              uploading={uploading}
              accept="image/*"
              enableClipboardPaste
              onClipboardPasteError={handleClipboardPasteError}
            />
          </Box>
        )}

        {/* Loading skeleton (Demo: 140×~100 Kacheln) */}
        {loading && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" width={140} height={124} />
            ))}
          </Box>
        )}

        {/* Empty state */}
        {!loading && files.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Keine Screenshots vorhanden.
          </Typography>
        )}

        {/* Bilder: Demo-Galerie (Kacheln + Referenz-Badge) */}
        {!loading && imageFiles.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {imageFiles.map((file, fileIdx) => {
              const used = usedFileIds.has(file.id);
              const edited = isEditedVersion(file);
              return (
                <Box key={file.id} sx={{ width: 140 }} data-testid={`gallery-tile-${file.id}`}>
                  <Box
                    onClick={() => openLightboxAtIndex(fileIdx)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `2px solid ${used ? alpha(theme.palette.primary.main, 0.4) : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <VersionedImg
                      fileId={file.id}
                      src={api.getPreviewUrl(file.id)}
                      alt={file.dateiname}
                      sx={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                    />
                    {edited && (
                      <Tooltip
                        title={
                          file.original_file_id && files.some((f) => f.id === file.original_file_id)
                            ? `Bearbeitete Version von „${files.find((f) => f.id === file.original_file_id)?.dateiname}“`
                            : 'Bearbeitete Version (Original nicht mehr vorhanden)'
                        }
                        arrow
                      >
                        <Chip
                          icon={<BrushIcon sx={{ fontSize: 12 }} />}
                          label="Bearbeitet"
                          size="small"
                          color="primary"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </Tooltip>
                    )}
                    {used && (
                      <Tooltip title="Im Text referenziert" arrow>
                        <CheckCircleIcon
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            fontSize: 16,
                            color: theme.palette.primary.main,
                            bgcolor: isDark ? '#181c34' : '#fff',
                            borderRadius: '50%',
                          }}
                        />
                      </Tooltip>
                    )}
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        display: 'block',
                        px: 0.75,
                        py: 0.5,
                        fontSize: '0.6rem',
                        color: 'text.secondary',
                        bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
                      }}
                      title={file.dateiname}
                    >
                      {file.dateiname}
                    </Typography>
                  </Box>
                  {editable ? (
                    <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                      {onInsertImage && (
                        <Tooltip title="Als Bild in Editor einfügen" arrow>
                          <IconButton
                            data-testid={`btn-insert-image-${file.id}`}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInsertImage(file);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <InsertPhotoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEditImages && (
                        <Tooltip
                          title={edited ? 'Bearbeitung anpassen (Formen verschieben, löschen, ergänzen)' : 'Bearbeiten: Rahmen, Pfeile, Verpixeln, Zuschneiden … (Original bleibt erhalten)'}
                          arrow
                        >
                          <span>
                            <IconButton
                              data-testid={`btn-edit-image-${file.id}`}
                              size="small"
                              disabled={editingFileId !== null}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleEditImage(file);
                              }}
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <BrushIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Herunterladen" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            api.download(file.id);
                          }}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canDeleteFiles && (
                        <Tooltip title="Löschen" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(file);
                            }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <Tooltip title="Herunterladen" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            api.download(file.id);
                          }}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Nicht-Bild-Anhänge (Burp/Code etc.): kompakte Zeile — in der Demo nur Bilder */}
        {!loading && nonImageFiles.length > 0 && (
          <Box sx={{ mt: imageFiles.length > 0 ? 2 : 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 600,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                display: 'block',
                mb: 1,
              }}
            >
              Weitere Dateien
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {nonImageFiles.map((file) => (
                <Box
                  key={file.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${borderColor}`,
                    bgcolor: isDark
                      ? alpha(theme.palette.background.paper, 0.4)
                      : alpha(theme.palette.grey[50], 0.8),
                  }}
                >
                  <AddPhotoAlternateIcon sx={{ fontSize: 32, color: theme.palette.text.disabled, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={file.dateiname} sx={{ fontWeight: 500 }}>
                      {file.dateiname}
                    </Typography>
                    {file.dateigroesse != null && (
                      <Typography variant="caption" color="text.secondary">
                        {(file.dateigroesse / 1024).toFixed(0)} KB
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    {editable && onInsertCodeBlock && isCodeFile(file) && (
                      <Tooltip title="Als Code-Block in Editor einfügen" arrow>
                        <IconButton size="small" onClick={() => handleInsertCodeBlock(file)} sx={{ color: theme.palette.primary.main }}>
                          <CodeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Herunterladen" arrow>
                      <IconButton size="small" onClick={() => api.download(file.id)} sx={{ color: theme.palette.text.secondary }}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {editable && canDeleteFiles && (
                      <Tooltip title="Löschen" arrow>
                        <IconButton size="small" onClick={() => handleDeleteClick(file)} sx={{ color: theme.palette.error.main }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <ConfirmDialog
          open={deleteDialogOpen}
          title="Screenshot löschen"
          message={
            fileToDelete && files.some((f) => f.original_file_id === fileToDelete.id)
              ? `Möchten Sie das Original "${fileToDelete.dateiname}" wirklich löschen? Bearbeitete Versionen bleiben erhalten, können danach aber nicht mehr nachbearbeitet werden.`
              : `Möchten Sie "${fileToDelete?.dateiname}" wirklich löschen?`
          }
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setFileToDelete(null);
          }}
        />

        {/* Lightbox */}
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              bgcolor: isDark ? '#0d1025' : '#111',
              width: '92vw',
              height: '90vh',
              maxWidth: '92vw',
              maxHeight: '90vh',
              borderRadius: 3,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ color: '#aaa', fontWeight: 500, flex: 1 }} noWrap>
              {imageFiles[lightboxIndex]?.dateiname || ''}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', mr: 2 }}>
              {imageFiles.length > 0 ? `${lightboxIndex + 1} / ${imageFiles.length}` : ''}
            </Typography>
            {canEditImages && imageFiles[lightboxIndex] && (
              <Tooltip title="Bild bearbeiten" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={editingFileId !== null}
                    onClick={() => {
                      const f = imageFiles[lightboxIndex];
                      setLightboxOpen(false);
                      void handleEditImage(f);
                    }}
                    sx={{ color: '#aaa', mr: 1 }}
                    data-testid="lightbox-edit-image"
                  >
                    <BrushIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setLightboxOpen(false)} sx={{ color: '#aaa' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Main image */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 0, px: 2 }}>
            {imageFiles.length > 1 && (
              <IconButton
                onClick={() => setLightboxIndex((prev) => (prev - 1 + imageFiles.length) % imageFiles.length)}
                sx={{ position: 'absolute', left: 8, zIndex: 2, color: '#ccc', bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}

            {imageFiles[lightboxIndex] && (
              <Fade in key={imageFiles[lightboxIndex].id}>
                <Box sx={{ maxWidth: '100%', maxHeight: '100%', display: 'flex' }}>
                  <VersionedImg
                    fileId={imageFiles[lightboxIndex].id}
                    src={api.getPreviewUrl(imageFiles[lightboxIndex].id)}
                    alt={imageFiles[lightboxIndex].dateiname}
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }}
                  />
                </Box>
              </Fade>
            )}

            {imageFiles.length > 1 && (
              <IconButton
                onClick={() => setLightboxIndex((prev) => (prev + 1) % imageFiles.length)}
                sx={{ position: 'absolute', right: 8, zIndex: 2, color: '#ccc', bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
          </Box>

          {/* Thumbnail strip (wie Demo-Ansicht) */}
          {imageFiles.length > 1 && (
            <Box sx={{
              flexShrink: 0,
              display: 'flex',
              gap: 1,
              px: 2,
              py: 1.5,
              overflowX: 'auto',
              justifyContent: 'center',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
            }}>
              {imageFiles.map((file, i) => {
                const isActive = i === lightboxIndex;
                const isUsed = usedFileIds.has(file.id);
                return (
                  <Box
                    key={file.id}
                    onClick={() => setLightboxIndex(i)}
                    sx={{
                      width: 72,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: isActive ? `2px solid ${LIGHTBOX_THUMB_ACTIVE}` : '2px solid transparent',
                      opacity: isActive ? 1 : 0.6,
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <VersionedImg
                      fileId={file.id}
                      src={api.getPreviewUrl(file.id)}
                      alt={file.dateiname}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {isUsed && (
                      <CheckCircleIcon sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        fontSize: 12,
                        color: LIGHTBOX_THUMB_ACTIVE,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                      }} />
                    )}
                    {!isUsed && (
                      <Box sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        border: '1.5px solid rgba(255,255,255,0.4)',
                        bgcolor: 'rgba(0,0,0,0.4)',
                      }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ScreenshotGallery;
